import React, { useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Moment from 'react-moment';

import { makeGetTask } from 'selectors/tasks';
import { makeGetMilestone } from 'selectors/milestones';

import CanUser from 'components/can-user';
import DateElement from 'components/date-element';
import DateTime from 'components/DateTime';

import IconCalendarSimple from 'icons/icon-calendar-simple';

const TaskDueDate = ({ id, dueDate, startDate, completed, milestone, createdBy, withIcon, withFromNow, onChange }) => {
    const dateCtrl = useRef(null);

    const handleClick = useCallback(() => {
        if (!dateCtrl.current) {
            return;
        }

        dateCtrl.current.show();
    }, [dateCtrl])

    const handleChange = useCallback((nextDate, updateStartDate) => {
        let nextStartDateFormatted;

        if (updateStartDate) {
            const diff = Math.max(0, moment(dueDate).diff(startDate, 'days')) || 2;

            nextStartDateFormatted = moment(nextDate).add(-diff, 'days').toISOString();
        }

        const nextDateFormatted = nextDate && moment(nextDate).toISOString();

        if (dueDate === nextDateFormatted) {
            return;
        }

        onChange({
            nextDate: nextDateFormatted,
            nextStartDate: nextStartDateFormatted,
            milestone
        });
        dateCtrl && dateCtrl.current.hide();
    }, [dueDate, startDate, milestone, onChange, dateCtrl])

    const handleRemove = useCallback(() => {
        onChange({
            nextDate: null
        });
        dateCtrl && dateCtrl.current.hide();
    }, [onChange, dateCtrl]);

    if (!dueDate && !onChange) {
        return null;
    }

    return (
        <div className={'task-due-date meta-item task-action' + (moment().diff(dueDate) > 0 && !completed ? ' text-warning' : '')} onClick={handleClick}>
            {
                withIcon ? (
                    <span className="meta-icon">
                        <IconCalendarSimple />
                    </span>
                ) : null
            }

            {
                dueDate ? (
                    <React.Fragment>
                        <DateTime format="dateTime">{dueDate}</DateTime>

                        {
                            withFromNow ? (
                                <small className="from-now"><Moment fromNow>{dueDate}</Moment></small>
                            ) : null
                        }
                    </React.Fragment>
                ) : (
                    <CanUser type="editTask" customList={[createdBy]}>
                        <span className="placeholder action-text">
                            Add
                        </span>
                    </CanUser>
                )
            }

            {
                onChange ? (
                    <CanUser type="editTask" customList={[createdBy]}>
                        <DateElement
                            selected={dueDate}
                            title={dueDate ? 'CHANGE DUE DATE' : 'ADD A DUE DATE'}
                            xAlign="right"
                            defaultXPosition="left"
                            removePopoutTitle="REMOVE DUE DATE"
                            removeBodyText="Are you sure you want to remove the due date from this task?"
                            relatedAfter={startDate}
                            relatedCopy="Due date is before task start date."
                            relatedConfirmText="Update both"
                            showTimeSelect={true}
                            showQuickOptions={true}
                            onChange={handleChange}
                            onRemove={handleRemove}
                            provideController={dateCtrl}
                        />
                    </CanUser>
                ) : null
            }
        </div>
    );
};

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();

    return ({ tasks, milestones }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);

        return {
            completed: task?.completed,
            dueDate: task?.due_date,
            startDate: task?.start_date,
            createdBy: task?.created_by,
            milestone: task?.milestone && getMilestone(milestones.items, task.milestone)
        }
    }
}

export default connect(makeMapStateToProps)(React.memo(TaskDueDate));
